import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Teacher } from '../types';
import { getTeacherMe, updateTeacherLanguage } from '../services/api';

interface TeacherAuthContextType {
  teacher: Teacher | null;
  /** Current access token. The api.ts interceptor refreshes it transparently. */
  token: string | null;
  isLoading: boolean;
  /** Preferred "Ask the coach" reply language ('en' when unset). */
  language: 'en' | 'hi';
  /** Persist a new coach-reply language (server-side + local state). */
  setLanguage: (language: 'en' | 'hi') => Promise<void>;
  /** Called by the login/register screen on success. Stores both tokens. */
  loginTeacher: (token: string, refreshToken: string, teacher: Teacher) => void;
  updateTeacher: (teacher: Teacher) => void;
  logoutTeacher: () => void;
}

const TOKEN_KEY = 'teacherToken';
const REFRESH_TOKEN_KEY = 'teacherRefreshToken';
const TEACHER_KEY = 'teacher';

const TeacherAuthContext = createContext<TeacherAuthContextType | undefined>(undefined);

export const useTeacherAuth = (): TeacherAuthContextType => {
  const ctx = useContext(TeacherAuthContext);
  if (ctx === undefined) {
    throw new Error('useTeacherAuth must be used within a TeacherAuthProvider');
  }
  return ctx;
};

export const TeacherAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [teacher, setTeacher] = useState<Teacher | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Listen for the `teacher-session-expired` event the axios interceptor
  // dispatches when the refresh token itself is rejected (90d idle). This
  // is the only "force logout" trigger — the access token expiring is
  // handled silently by the interceptor and never reaches us.
  useEffect(() => {
    const onExpired = () => {
      setToken(null);
      setTeacher(null);
    };
    window.addEventListener('teacher-session-expired', onExpired);
    return () => window.removeEventListener('teacher-session-expired', onExpired);
  }, []);

  // Keep our in-memory token in step with silent refreshes. Components pass
  // `token` explicitly into api calls, so if we kept the old one here they'd
  // send a dead token forever after the interceptor rotates localStorage.
  useEffect(() => {
    const onRefreshed = (e: Event) => {
      const next = (e as CustomEvent<string>).detail;
      if (next) setToken(next);
    };
    window.addEventListener('teacher-token-refreshed', onRefreshed);
    return () => window.removeEventListener('teacher-token-refreshed', onRefreshed);
  }, []);

  useEffect(() => {
    const restore = async () => {
      const storedToken = localStorage.getItem(TOKEN_KEY);
      if (!storedToken) {
        setIsLoading(false);
        return;
      }
      try {
        // Verify the token is still valid and refresh the profile.
        // If the access token is dead but the refresh token is alive, the
        // axios interceptor will silently mint a new access token and
        // localStorage will be updated before this call returns.
        const verified = await getTeacherMe(storedToken);
        const liveToken = localStorage.getItem(TOKEN_KEY) || storedToken;
        setToken(liveToken);
        setTeacher(verified);
        localStorage.setItem(TEACHER_KEY, JSON.stringify(verified));
      } catch (err) {
        // Either the access token AND refresh token are both gone — full
        // session expiry. Wipe and let the route guard send them to login.
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(TEACHER_KEY);
        setToken(null);
        setTeacher(null);
      } finally {
        setIsLoading(false);
      }
    };
    restore();
  }, []);

  const loginTeacher = (newToken: string, newRefreshToken: string, newTeacher: Teacher) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
    localStorage.setItem(TEACHER_KEY, JSON.stringify(newTeacher));
    setToken(newToken);
    setTeacher(newTeacher);
  };

  const updateTeacher = (updated: Teacher) => {
    localStorage.setItem(TEACHER_KEY, JSON.stringify(updated));
    setTeacher(updated);
  };

  const logoutTeacher = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(TEACHER_KEY);
    setToken(null);
    setTeacher(null);
  };

  // Mirrors the student AuthContext: persist server-side, then sync the
  // cached teacher object so the choice survives reloads.
  const language: 'en' | 'hi' = teacher?.language === 'hi' ? 'hi' : 'en';
  const setLanguage = async (next: 'en' | 'hi') => {
    if (!token) throw new Error('Not logged in');
    const updated = await updateTeacherLanguage(token, next);
    updateTeacher(updated);
  };

  return (
    <TeacherAuthContext.Provider
      value={{ teacher, token, isLoading, language, setLanguage, loginTeacher, updateTeacher, logoutTeacher }}
    >
      {children}
    </TeacherAuthContext.Provider>
  );
};
